import React, { useCallback, useState } from 'react';
import cn from '@/utils/cn';
import KPIStrip from './KPIStrip';
import Charts from './Charts';
import EndedSessionsTable from './EndedSessionsTable';
import { ActiveFiltersPopover, EndedFiltersPopover } from './FiltersPopover';
import PaginationControls from './PaginationControls';
import SessionDetailPIP from './SessionDetailPIP';
import SessionsTable from './SessionsTable';
import { SearchInput, toggleSortDirection } from './tableShared';
import useTableControls, { DEFAULT_ACTIVE_CONTROLS, DEFAULT_ENDED_CONTROLS } from './useTableControls';
import {
  useLiveSessions, useLiveSessionsMetrics, useProgramOptions, useRecentlyEndedSessions,
} from './hooks';
import type { ActiveControls, EndedControls } from './types';

const PER_PAGE = 25;

const ERROR_BANNER_CLASS = cn(
  'tw-bg-red-50 tw-border tw-border-solid tw-border-red-200',
  'tw-text-red-700 tw-rounded-lg tw-p-4',
  'tw-flex tw-items-center tw-justify-between',
);

const RETRY_BUTTON_CLASS = cn(
  'tw-bg-white tw-border tw-border-solid tw-border-red-300',
  'tw-text-red-700 tw-rounded tw-px-3 tw-py-1 tw-text-sm',
  'hover:tw-bg-red-100',
);

const tabButtonClass = (active: boolean) => cn(
  'tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-cursor-pointer',
  'tw-border-0 tw-border-b-2 tw-border-solid tw-bg-transparent',
  active
    ? 'tw-border-tci-purple tw-text-gray-900'
    : 'tw-border-transparent tw-text-gray-500 hover:tw-text-gray-700',
);

const RefreshIndicator = ({ isFetching }: { isFetching: boolean }) => (
  <span
    aria-label={isFetching ? 'Refreshing' : 'Idle'}
    className="tw-flex tw-items-center tw-gap-2 tw-text-xs tw-text-gray-500"
  >
    <span
      aria-hidden
      className={`tw-h-2 tw-w-2 tw-rounded-full ${isFetching ? 'tw-bg-blue-500 tw-animate-pulse' : 'tw-bg-green-500'}`}
    />
    Auto-refreshing every 5s
  </span>
);

const ErrorState = ({ onRetry }: { onRetry: () => void }) => (
  <div className={ERROR_BANNER_CLASS}>
    <span>Failed to load live session data.</span>
    <button className={RETRY_BUTTON_CLASS} type="button" onClick={onRetry}>
      Retry
    </button>
  </div>
);

const activeFilterCount = (c: ActiveControls): number => (
  c.filters.heartbeat.length + c.filters.programIds.length
);

const endedFilterCount = (c: EndedControls): number => {
  let count = c.filters.endReason.length + c.filters.programIds.length;
  if (c.filters.withinHours !== DEFAULT_ENDED_CONTROLS.filters.withinHours) count += 1;
  return count;
};

const LiveSessionsMonitor = () => {
  const {
    activeControls, endedControls, setActiveControls, setEndedControls, setTableMode, tableMode,
  } = useTableControls();
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);

  const sessionsQuery = useLiveSessions({ controls: activeControls, perPage: PER_PAGE });
  const metricsQuery = useLiveSessionsMetrics();
  const endedQuery = useRecentlyEndedSessions({ controls: endedControls, perPage: PER_PAGE });
  const programsQuery = useProgramOptions();

  const hasError = sessionsQuery.isError || metricsQuery.isError;
  const isFetching = sessionsQuery.isFetching || metricsQuery.isFetching || endedQuery.isFetching;
  const activeTotal = sessionsQuery.data?.meta.total_count ?? 0;
  const endedTotal = endedQuery.data?.meta.total_count ?? 0;

  const onActiveSort = useCallback((key: string) => {
    setActiveControls(prev => ({
      ...prev,
      page: 1,
      sort: toggleSortDirection(prev.sort, key),
    }));
  }, [setActiveControls]);

  const onEndedSort = useCallback((key: string) => {
    setEndedControls(prev => ({
      ...prev,
      page: 1,
      sort: toggleSortDirection(prev.sort, key),
    }));
  }, [setEndedControls]);

  const setActiveQ = useCallback((q: string) => {
    setActiveControls(prev => ({ ...prev, page: 1, q }));
  }, [setActiveControls]);

  const setEndedQ = useCallback((q: string) => {
    setEndedControls(prev => ({ ...prev, page: 1, q }));
  }, [setEndedControls]);

  return (
    <div className="tw-bg-gray-50 tw-min-h-screen tw-p-6">
      <div className="tw-flex tw-flex-col tw-gap-5">
        <div className="tw-flex tw-items-end tw-justify-between tw-flex-wrap tw-gap-2">
          <div>
            <h1 className="tw-text-2xl tw-font-semibold tw-text-gray-900 tw-m-0">
              Live Sessions Monitor
            </h1>
            <p className="tw-text-sm tw-text-gray-500 tw-mt-1 tw-mb-0">
              Teacher-paced slideshow sessions currently running across all subscribers.
            </p>
          </div>
          <RefreshIndicator isFetching={isFetching} />
        </div>

        {hasError && (
          <ErrorState
            onRetry={() => {
              sessionsQuery.refetch();
              metricsQuery.refetch();
            }}
          />
        )}

        <KPIStrip isLoading={metricsQuery.isLoading} metrics={metricsQuery.data} />

        <Charts metrics={metricsQuery.data} onSelectSession={setSelectedSessionId} />

        <div>
          <div
            aria-label="Session list mode"
            className="tw-flex tw-items-end tw-gap-1 tw-mb-3 tw-border-0 tw-border-b tw-border-solid tw-border-gray-200"
            role="tablist"
          >
            <button
              aria-selected={tableMode === 'active'}
              className={tabButtonClass(tableMode === 'active')}
              role="tab"
              type="button"
              onClick={() => setTableMode('active')}
            >
              Active ({activeTotal.toLocaleString()})
            </button>
            <button
              aria-selected={tableMode === 'ended'}
              className={tabButtonClass(tableMode === 'ended')}
              role="tab"
              type="button"
              onClick={() => setTableMode('ended')}
            >
              Recently ended ({endedTotal.toLocaleString()})
            </button>
          </div>

          {tableMode === 'active' ? (
            <>
              <div className="tw-flex tw-items-center tw-gap-2 tw-mb-3">
                <SearchInput
                  placeholder="Search by teacher, program, or slideshow"
                  value={activeControls.q}
                  onChange={setActiveQ}
                />
                <ActiveFiltersPopover
                  filterCount={activeFilterCount(activeControls)}
                  heartbeat={activeControls.filters.heartbeat}
                  programIds={activeControls.filters.programIds}
                  programs={programsQuery.data ?? []}
                  onChangeHeartbeat={heartbeat => setActiveControls(prev => ({
                    ...prev, filters: { ...prev.filters, heartbeat }, page: 1,
                  }))}
                  onChangeProgramIds={programIds => setActiveControls(prev => ({
                    ...prev, filters: { ...prev.filters, programIds }, page: 1,
                  }))}
                  onClearAll={() => setActiveControls(prev => ({
                    ...prev, filters: DEFAULT_ACTIVE_CONTROLS.filters, page: 1,
                  }))}
                />
              </div>
              <SessionsTable
                isLoading={sessionsQuery.isLoading}
                selectedSessionId={selectedSessionId}
                sessions={sessionsQuery.data?.data ?? []}
                sort={activeControls.sort}
                onSelectSession={setSelectedSessionId}
                onSort={onActiveSort}
              />
              {sessionsQuery.data && (
                <PaginationControls
                  page={sessionsQuery.data.meta.page}
                  perPage={sessionsQuery.data.meta.per_page}
                  totalCount={sessionsQuery.data.meta.total_count}
                  totalPages={sessionsQuery.data.meta.total_pages}
                  onPageChange={page => setActiveControls(prev => ({ ...prev, page }))}
                />
              )}
            </>
          ) : (
            <>
              <div className="tw-flex tw-items-center tw-gap-2 tw-mb-3">
                <SearchInput
                  placeholder="Search by teacher, program, or slideshow"
                  value={endedControls.q}
                  onChange={setEndedQ}
                />
                <EndedFiltersPopover
                  endReason={endedControls.filters.endReason}
                  filterCount={endedFilterCount(endedControls)}
                  programIds={endedControls.filters.programIds}
                  programs={programsQuery.data ?? []}
                  withinHours={endedControls.filters.withinHours}
                  onChangeEndReason={endReason => setEndedControls(prev => ({
                    ...prev, filters: { ...prev.filters, endReason }, page: 1,
                  }))}
                  onChangeProgramIds={programIds => setEndedControls(prev => ({
                    ...prev, filters: { ...prev.filters, programIds }, page: 1,
                  }))}
                  onChangeWithinHours={withinHours => setEndedControls(prev => ({
                    ...prev, filters: { ...prev.filters, withinHours }, page: 1,
                  }))}
                  onClearAll={() => setEndedControls(prev => ({
                    ...prev, filters: DEFAULT_ENDED_CONTROLS.filters, page: 1,
                  }))}
                />
              </div>
              <EndedSessionsTable
                isLoading={endedQuery.isLoading}
                selectedSessionId={selectedSessionId}
                sessions={endedQuery.data?.data ?? []}
                sort={endedControls.sort}
                onSelectSession={setSelectedSessionId}
                onSort={onEndedSort}
              />
              {endedQuery.data && (
                <PaginationControls
                  page={endedQuery.data.meta.page}
                  perPage={endedQuery.data.meta.per_page}
                  totalCount={endedQuery.data.meta.total_count}
                  totalPages={endedQuery.data.meta.total_pages}
                  onPageChange={page => setEndedControls(prev => ({ ...prev, page }))}
                />
              )}
            </>
          )}
        </div>
      </div>
      {selectedSessionId && (
        <SessionDetailPIP
          sessionId={selectedSessionId}
          onClose={() => setSelectedSessionId(null)}
        />
      )}
    </div>
  );
};

export default LiveSessionsMonitor;
